import { hasCardNumber } from '../../../utils/cardNumber'
import { formatRarityLabel, getGameAccentClassName, getRarityClassName, shouldRenderRarity } from '../../../components/ui/CardInfoPanel'
import { getConditionClassName, getPrintingClassName } from '../../../utils/inventoryVariantClassNames'
import { getCustomCategory } from '../../../catalog/categoryRegistry'
import { normalizePreReleaseMode, type PreReleaseMode } from '../../../utils/preRelease'
import { normalizeDisplayCardName } from './inventoryDisplayUtils'
import type { InventoryProduct } from './inventoryTypes'
import type { Currency } from '../../../contexts/CurrencyContext'
import {
  getManualPriceTimingLabel,
  getSaleConfig,
  getSaleTimingLabel,
  isManualPriceActive,
  isSaleActiveNow,
  isSaleConfigured,
  type InventoryPricingContext,
} from './pricing'

export type InventoryStylesModule = Record<string, string>

export type InventoryRowDerived = {
  manualActive: boolean
  saleConfigured: boolean
  saleActive: boolean
  saleTimingLabel: string | null
  manualPriceTimingLabel: string | null
  saleType: 'percentage' | 'fixed' | undefined
  saleValue: number | undefined
  saleScheduled: boolean
  showSaleIndicators: boolean
  hasManualPriceConfigured: boolean
  displayCardName: string
  /**
   * Currency of every store figure below (the listing's own). Null when the server could not
   * price the row — every figure is then null too and renders as "price unavailable".
   */
  storeCurrency: string | null
  /** Tracked market price, before the floor (store currency, listing rate). */
  marketPriceCents: number | null
  /** Market price lifted to the minimum floor — what auto pricing lists before sale and markup. */
  autoPriceCents: number | null
  minimumFloorCents: number | null
  /** Active manual price: the amount typed when known ({@link manualPriceExact}), else converted at the listing rate. */
  manualPriceCents: number | null
  manualPriceExact: boolean
  /** Cost basis at the listing rate; always a conversion from {@link costBasisUsdCents}. */
  costBasisCents: number | null
  costBasisUsdCents: number | null
  /** Auto price minus cost basis, both in store currency at the same rate. */
  profitCents: number | null
  /** The rate every store figure on this row used (the listing's pinned rate when published). */
  listingUsdToStoreRate: number | null
  /** Pre-sale ("was") listed price, in {@link InventoryRowDerived.effectivePriceCurrency}. */
  saleBasePriceCents: number | null
  /** Listed sale price, in {@link InventoryRowDerived.effectivePriceCurrency}. */
  salePriceCents: number | null
  /**
   * The listed price, in {@link InventoryRowDerived.effectivePriceCurrency} — always the
   * server-resolved `listedPrice`, so it equals what the storefront shows. Null when the server
   * could not price it (no exchange rate): render "price unavailable", never a guess.
   */
  effectivePriceCents: number | null
  /** Currency `effectivePriceCents` is denominated in; null with it. */
  effectivePriceCurrency: string | null
  /** False when the server sent no listed price (shown as unavailable). */
  isServerPricedListing: boolean
  /** Platform markup folded into the listed price, so the UI can explain it. */
  listingMarkupPercent: number
  isMinimumFloorApplied: boolean
  manualDiffCents: number | null
  marketDiffCents: number | null
  hasMarketComparison: boolean
  marketPreviousCents: number | null
  saleLabel: string | null
  saleDiscountCents: number | null
  saleMetaPrimary: string | null
  saleBadgeSuffix: string
  isCustom: boolean
  customCategoryLabel: string | null
  productGameName: string | null
  productSetName: string | null
  productSetCode: string | null
  productRarity: string | null
  productRarityLabel: string
  showRarityBadge: boolean
  productSetNumber: string | null
  hasVariantChips: boolean
  gameAccentClassName: string
  rarityClassName: string
  primaryImageUrl: string | null
  primaryImageAlt: string
  printingClassKey: ReturnType<typeof getPrintingClassName>
  conditionClassKey: ReturnType<typeof getConditionClassName>
  /** True when the product's set release date is in the future. */
  isPreRelease: boolean
  /** Listing mode while unreleased; only meaningful when {@link isPreRelease}. */
  preReleaseMode: PreReleaseMode
  /** Localised set release date, when known and in the future. */
  preReleaseDateLabel: string | null
  /** True when the owner has forced the Shopify listing to draft (hidden). */
  isListingDrafted: boolean
  /**
   * True when the product overrides the store-wide sales-channel selection.
   * Worth a badge because it is otherwise invisible from the list — a product
   * deliberately held off a channel looks identical to one that is everywhere.
   */
  hasChannelOverride: boolean
  /** The chosen channel count, for the badge's tooltip. */
  channelOverrideCount: number
  /** Localised removal time for an event product, when it has one. */
  eventRemovalLabel: string | null
}

export function computeInventoryRowDerived(
  product: InventoryProduct,
  ctx: InventoryPricingContext & { styles: InventoryStylesModule },
): InventoryRowDerived {
  const { formatPrice, styles } = ctx

  const manualActive = isManualPriceActive(product)
  const saleConfigured = isSaleConfigured(product)
  const saleActive = isSaleActiveNow(product)
  const saleTimingLabel = getSaleTimingLabel(product)
  const manualPriceTimingLabel = getManualPriceTimingLabel(product)
  const { saleType, saleValue } = getSaleConfig(product)

  const saleStartAt = product.saleStartAt ?? product.saleInfo?.saleStartAt ?? null
  const saleEndAt = product.saleEndAt ?? product.saleInfo?.saleEndAt ?? null
  const nowMs = Date.now()
  const startMs = saleStartAt ? new Date(saleStartAt).getTime() : null
  const endMs = saleEndAt ? new Date(saleEndAt).getTime() : null
  const saleScheduled =
    saleConfigured &&
    !saleActive &&
    typeof startMs === 'number' &&
    !Number.isNaN(startMs) &&
    startMs > nowMs &&
    (endMs === null || (typeof endMs === 'number' && !Number.isNaN(endMs) && endMs > nowMs))
  const showSaleIndicators = saleActive || saleScheduled

  const hasManualPriceConfigured =
    typeof product.manualPriceCents === 'number' && Number.isFinite(product.manualPriceCents) && product.manualPriceCents > 0

  const rawCardName = product.card?.name || product.name || 'Card'
  const displayCardName = normalizeDisplayCardName(rawCardName, product.card?.setNumber)

  // ── The listed price ───────────────────────────────────────────────────────
  //
  // Always the server's `listedPrice`. There used to be a client-side fallback
  // derivation for responses without it; it could not see the platform markup,
  // converted with a browser-cached rate, and rounded percentage sales
  // differently from the backend — so it disagreed with the storefront exactly
  // when it was used. A missing `listedPrice` now means "price unavailable".
  //
  // `effectivePriceCurrency` travels with the amount so callers format it
  // correctly. When the viewer's display currency matches the organisation's —
  // the ordinary case — `formatPrice` passes the value through untouched and the
  // screen shows precisely the string that was pushed to Shopify.
  const listedPrice = product.listedPrice ?? null
  const effectivePriceCents = listedPrice ? listedPrice.priceCents : null
  const effectivePriceCurrency = listedPrice ? listedPrice.currency : null
  const isServerPricedListing = listedPrice !== null

  // The sale figures are the listing's own: sale price = listed price, "was" = its compare-at.
  const salePriceCents = saleActive && listedPrice ? listedPrice.priceCents : null
  const saleBasePriceCents = saleActive && listedPrice ? listedPrice.compareAtPriceCents : null
  const listingMarkupPercent = listedPrice?.markupPercent ?? 0

  // ── Every other money figure on the row ────────────────────────────────────
  //
  // Market, auto, floor, manual, cost and the differences between them all come
  // from the server's `storePrices`, resolved by the same engine at the SAME rate
  // as the listed price. They used to be converted here from their USD columns at
  // the browser's live rate, beside a listed price at the pinned rate — so a box
  // listed at exactly the A$1,100.00 its owner typed read "Manual price
  // A$1,115.71" on the same card. Nothing below converts a currency; differences
  // are subtractions of same-currency, same-rate figures.
  const figures = listedPrice ? (product.storePrices ?? null) : null
  const storeCurrency = figures?.currency ?? null
  const marketPriceCents = figures?.marketPriceCents ?? null
  const autoPriceCents = figures?.autoPriceCents ?? null
  const minimumFloorCents = figures?.minimumFloorCents ?? null
  const isMinimumFloorApplied = figures?.isMinimumFloorApplied ?? false
  const manualPriceCents = manualActive ? (figures?.manualPrice?.cents ?? null) : null
  const manualPriceExact = manualActive && Boolean(figures?.manualPrice?.exact)
  const manualDiffCents = manualActive ? (figures?.manualVsMarketCents ?? null) : null
  const costBasisCents = figures?.costBasis?.cents ?? null
  const costBasisUsdCents = figures?.costBasis?.convertedFromUsdCents ?? null
  const profitCents = figures?.autoVsCostCents ?? null
  const listingUsdToStoreRate = figures?.usdToStoreRate ?? null

  // The backend computes priceChange24h from a single tracking source and zeroes it
  // when the last recorded move is older than 24h, and converts it at the listing rate.
  const marketDiffCents = figures?.marketChange24hCents ?? null
  const hasMarketComparison = typeof marketDiffCents === 'number' && marketPriceCents !== null
  const marketPreviousCents = hasMarketComparison ? marketPriceCents! - marketDiffCents! : null

  const fixedOffCents = figures?.fixedSaleOffCents ?? null
  const saleLabel =
    saleConfigured && saleType && typeof saleValue === 'number'
      ? saleType === 'percentage'
        ? `${saleValue}% off`
        : fixedOffCents !== null && storeCurrency !== null
          ? `${formatPrice(fixedOffCents, storeCurrency as Currency)} off`
          : 'Amount off'
      : null

  const saleDiscountCents =
    saleActive && saleBasePriceCents !== null && salePriceCents !== null ? Math.max(0, saleBasePriceCents - salePriceCents) : null

  const saleMetaPrimary = saleTimingLabel ? `Ends ${saleTimingLabel.replace(/^Ends\s+/i, '')}` : null
  const saleBadgeSuffix = saleLabel ? ` • ${saleLabel}` : ''
  // Custom (non-catalog) products carry brand/collection/category in variantData
  // instead of a linked card; surface those so they render properly.
  const variantData = product.variantData
  const isCustom = Boolean(variantData?.isCustom) || (!product.card && Boolean(variantData?.category))
  const customCategorySlug = typeof variantData?.category === 'string' ? variantData.category : null
  const customCategoryLabel = customCategorySlug ? (getCustomCategory(customCategorySlug)?.label ?? null) : null

  const productGameName =
    product.card?.set?.game?.name || (isCustom ? (variantData?.gameName || variantData?.brand || null) : null)
  const productSetName =
    product.card?.set?.setName || (isCustom ? (variantData?.collection || variantData?.setName || null) : null)
  const productSetCode = product.card?.set?.setCode || null
  const productRarity = product.card?.rarity || null
  const productRarityLabel = formatRarityLabel(productRarity)
  const showRarityBadge = shouldRenderRarity(productRarityLabel)
  const productSetNumber = hasCardNumber(product.card?.setNumber) ? product.card.setNumber : null
  const hasVariantChips = Boolean(
    productSetName ||
      productSetCode ||
      productGameName ||
      showRarityBadge ||
      productSetNumber ||
      customCategoryLabel ||
      product.variantData?.printing ||
      product.variantData?.condition,
  )
  const gameAccentClassName = styles[getGameAccentClassName(productGameName)] || styles.gameAccentDefault
  const rarityClassName = styles[getRarityClassName(productRarityLabel)] || styles.rarityDefault

  const primaryImageUrl =
    product.card?.imageUrl && product.card.imageUrl.length > 0
      ? product.card.imageUrl[0]
      : product.imageUrls && product.imageUrls.length > 0
        ? product.imageUrls[0]
        : null

  const primaryImageAlt = product.card?.name || product.name || 'Product'

  const printingClassKey = getPrintingClassName(product.variantData?.printing)
  const conditionClassKey = getConditionClassName(product.variantData?.condition)

  // Catalog products take their release date from the set; a custom pre-order has
  // no set, so it carries the date on variantData — the same field the Shopify
  // sync reads for card-less rows, so both paths agree on what is unreleased.
  const rawReleaseDate = product.card?.set?.releaseDate
    ?? (typeof variantData?.releaseDate === 'string' ? variantData.releaseDate : undefined)
  const releaseDateMs = rawReleaseDate ? new Date(rawReleaseDate).getTime() : NaN
  const isPreRelease = Number.isFinite(releaseDateMs) && releaseDateMs > nowMs
  const preReleaseMode = normalizePreReleaseMode(product.preReleaseMode)
  const preReleaseDateLabel = isPreRelease
    ? new Date(releaseDateMs).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
    : null
  const isListingDrafted = product.listingStatus === 'draft'
  const channelOverrideCount = product.salesChannels?.publicationIds.length ?? 0
  const hasChannelOverride = channelOverrideCount > 0

  // Time-limited products are removed automatically at their end time; showing
  // that deadline on the row is the only warning the owner gets before it
  // happens. `eventEndAt` is the pre-rename key, still on rows created earlier.
  const listingEndRaw = variantData?.listingEndAt ?? variantData?.eventEndAt
  const eventEndsAtRaw = typeof listingEndRaw === 'string' ? listingEndRaw : null
  const eventEndsAtMs = eventEndsAtRaw ? new Date(eventEndsAtRaw).getTime() : NaN
  const eventRemovalLabel = Number.isFinite(eventEndsAtMs)
    ? new Date(eventEndsAtMs).toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
      })
    : null

  return {
    manualActive,
    saleConfigured,
    saleActive,
    saleTimingLabel,
    manualPriceTimingLabel,
    saleType,
    saleValue,
    saleScheduled,
    showSaleIndicators,
    hasManualPriceConfigured,
    displayCardName,
    storeCurrency,
    marketPriceCents,
    autoPriceCents,
    minimumFloorCents,
    manualPriceCents,
    manualPriceExact,
    costBasisCents,
    costBasisUsdCents,
    profitCents,
    listingUsdToStoreRate,
    saleBasePriceCents,
    salePriceCents,
    effectivePriceCents,
    effectivePriceCurrency,
    isServerPricedListing,
    listingMarkupPercent,
    isMinimumFloorApplied,
    manualDiffCents,
    marketDiffCents,
    hasMarketComparison,
    marketPreviousCents,
    saleLabel,
    saleDiscountCents,
    saleMetaPrimary,
    saleBadgeSuffix,
    isCustom,
    customCategoryLabel,
    productGameName,
    productSetName,
    productSetCode,
    productRarity,
    productRarityLabel,
    showRarityBadge,
    productSetNumber,
    hasVariantChips,
    gameAccentClassName,
    rarityClassName,
    primaryImageUrl,
    primaryImageAlt,
    printingClassKey,
    conditionClassKey,
    isPreRelease,
    preReleaseMode,
    eventRemovalLabel,
    preReleaseDateLabel,
    isListingDrafted,
    hasChannelOverride,
    channelOverrideCount,
  }
}
